import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import {
  ApiFilterOperation,
  DetailContext,
  DetailHandle,
  DetailToolbar2,
  DetailToolbarProps,
  Evidence,
  updateItemFactory,
} from '@eas/common-web';
import { InternalDocument } from '@models';
import { DecisionState, DocumentType, EvidenceApiUrl, UIElement } from '@enums';
import { useInternalDocumentPermission } from '../document/document-permission';
import { DocumentToolbar } from '../document/document-toolbar';
import { useDocuments } from '../document/documents';
import { ActionBar } from './internal-document-actionbar';
import { useColumns } from './internal-document-columns';
import { Fields } from './internal-document-fields';
import { useValidationSchema } from './internal-document-schema';

export function InternalDocuments() {
  const intl = useIntl();
  const validationSchema = useValidationSchema();
  const columns = useColumns();

  const { evidenceBag } = useDocuments({
    evidenceProps: {
      version: 15,
      apiProps: {
        url: EvidenceApiUrl.DOCUMENTS,
        updateItem: updateItemFactory({
          postProcess: postprocessUpdateCall,
        }),
      },
      tableProps: {
        defaultPreFilters: [
          {
            field: 'type.id',
            operation: ApiFilterOperation.EQ,
            value: DocumentType.INTERNAL,
          },
          {
            field: 'canceledDate',
            operation: ApiFilterOperation.IS_NULL,
          },
        ],
        tableName: intl.formatMessage({
          id: 'ES__DOCUMENTS_INTERNAL__TABLE__TITLE',
          defaultMessage: 'Interní dokumenty',
        }),
        columns: columns,
      },
      detailProps: {
        FieldsComponent: Fields,
        validationSchema,
        ToolbarComponent: InternalDocumentToolbar,
        toolbarProps: {
          title: intl.formatMessage({
            id: 'ES__DOCUMENTS_INTERNAL__DETAIL__TITLE',
            defaultMessage: 'Interní dokument',
          }),
          showBtn: (button) => !['REMOVE', 'NEW'].includes(button),
          After: DocumentToolbar,
          ActionBar,
        },
      },
    },
  });

  return <Evidence {...evidenceBag} />;
}

function InternalDocumentToolbar(
  props: DetailToolbarProps<InternalDocument>
) {
  const { source } = useContext<DetailHandle<InternalDocument>>(DetailContext);
  const { hasPermission } = useInternalDocumentPermission(source.data);

  return (
    <DetailToolbar2
      {...props}
      disableBtn={(button) =>
        button === 'EDIT' &&
        !hasPermission(UIElement.InternalDocument.EDIT_DOCUMENT_BUTTON)
      }
    />
  );
}

const postprocessUpdateCall = async (
  _: InternalDocument,
  document: InternalDocument
) => {
  if (
    document.decision &&
    document.linkedDecision?.state !== DecisionState.CANCELED
  ) {
    const response = await fetch(
      `${EvidenceApiUrl.DOCUMENTS}/${document.id}/decision`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form: document.linkedDecision!.form!,
          decisionDate: document.linkedDecision!.decisionDate!,
          attachments: {
            fullTextDocx: document.linkedDecision!.attachments!.fullTextDocx!,
          },
        }),
      }
    );

    await response.json();
  }

  return document;
};
